import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { StaffResource } from "@/shared/permissions";

// Shared model for the Scholen data-console. Datasets are declarative descriptors: a column list
// (with default-visible/align/accessor/csv), quick filters, a search projection, and a CSV name.
// The page handles search/filter/sort/paginate; columns are toggleable + reorderable, persisted in
// localStorage per dataset. Data is small (dozens of schools) so everything is client-side.

export type Align = "left" | "right" | "center";

export type ColumnDef<Row> = {
  id: string;
  label: string;
  /** Picker category, e.g. "Basis", "Contact", "Leerlingen", "Programma". */
  group?: string;
  defaultVisible?: boolean;
  /** Cannot be hidden (the identity column). */
  alwaysVisible?: boolean;
  align?: Align;
  /** Render as monospace (BRIN, IDs). */
  mono?: boolean;
  accessor: (row: Row) => unknown;
  render?: (row: Row) => ReactNode;
  /** Value for CSV export (defaults to accessor). */
  csv?: (row: Row) => string | number | boolean | null | undefined;
  /** Value used for sorting (defaults to accessor). */
  sortValue?: (row: Row) => string | number | null | undefined;
  /** Display value used for per-column value filtering (defaults to the formatted accessor). */
  filterValue?: (row: Row) => string;
  /** Set false to drop this column from the per-column value filter (e.g. free-text/id columns). */
  filterable?: boolean;
};

export type QuickFilter<Row> = {
  id: string;
  label: string;
  predicate: (row: Row) => boolean;
};

export type DatasetDescriptor<Row> = {
  key: string;
  /** Dutch URL slug for deep-linking (the schools dataset lives at the base path). */
  slug?: string;
  label: string;
  shortLabel?: string;
  description?: string;
  /** Capability needed to view this dataset (school-data -> scholen, programme -> inschrijvingen). */
  resource: StaffResource;
  columns: ColumnDef<Row>[];
  quickFilters?: QuickFilter<Row>[];
  /** Lower-cased text a row contributes to global search. */
  searchText: (row: Row) => string;
  getRowId: (row: Row) => string;
  getRowHref?: (row: Row) => string | undefined;
  defaultSort?: { id: string; dir: "asc" | "desc" };
  /** Base filename for CSV (a date is appended). */
  csvName: string;
  /** Column id used for "Export per …" ZIP download. Groups rows by this column's value. */
  groupByColumn?: { id: string; label: string };
};

// ── CSV (Dutch-Excel friendly: UTF-8 BOM, ; delimiter, CRLF, quoted, formula-injection safe) ──

function csvCell(value: unknown): string {
  let s: string;
  if (value == null) s = "";
  else if (typeof value === "boolean") s = value ? "ja" : "nee";
  else if (Array.isArray(value)) s = value.join(" | ");
  else s = String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`; // neutralize spreadsheet formula injection
  return `"${s.replace(/"/g, '""')}"`;
}

export function buildCsv<Row>(columns: ColumnDef<Row>[], rows: Row[], delimiter = ";"): string {
  const header = columns.map((c) => csvCell(c.label)).join(delimiter);
  const lines = rows.map((r) =>
    columns.map((c) => csvCell(c.csv ? c.csv(r) : c.accessor(r))).join(delimiter),
  );
  return `﻿${[header, ...lines].join("\r\n")}\r\n`;
}

/** The display string a column contributes to per-column value filtering (matches the cell text for
 *  simple columns; columns can override via `filterValue`, e.g. dates → a formatted label). */
export function columnFilterValue<Row>(col: ColumnDef<Row>, row: Row): string {
  if (col.filterValue) return col.filterValue(row);
  const v = col.accessor(row);
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Ja" : "Nee";
  return String(v);
}

export type ExportFormat = "xlsx" | "csv";

const EXPORT_FORMAT_KEY = "kuvo.console.exportFormat";

export function getExportFormat(): ExportFormat {
  try {
    const v = localStorage.getItem(EXPORT_FORMAT_KEY);
    if (v === "csv") return "csv";
  } catch { /* ignore */ }
  return "xlsx";
}
export function setExportFormat(f: ExportFormat): void {
  try { localStorage.setItem(EXPORT_FORMAT_KEY, f); } catch { /* ignore */ }
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function cellValue<Row>(c: ColumnDef<Row>, r: Row): string | number | boolean {
  const v = c.csv ? c.csv(r) : c.accessor(r);
  if (v == null) return "";
  if (typeof v === "number" || typeof v === "boolean") return v;
  return String(v);
}

export async function downloadExcel<Row>(filename: string, columns: ColumnDef<Row>[], rows: Row[]): Promise<void> {
  const buf = await buildExcelBuffer(columns, rows);
  downloadBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
}

export function downloadCsv(filename: string, csv: string): void {
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), filename);
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9À-ɏ]+/gi, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

async function buildExcelBuffer<Row>(columns: ColumnDef<Row>[], rows: Row[]): Promise<ArrayBuffer> {
  const ExcelJS = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Data");
  ws.addTable({
    name: "Data",
    ref: "A1",
    headerRow: true,
    style: { theme: "TableStyleLight1", showRowStripes: true },
    columns: columns.map((c) => ({ name: c.label, filterButton: true })),
    rows: rows.map((r) => columns.map((c) => cellValue(c, r))),
  });
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i]!;
    let maxLen = col.label.length;
    for (const r of rows.slice(0, 50)) {
      const len = String(cellValue(col, r)).length;
      if (len > maxLen) maxLen = len;
    }
    ws.getColumn(i + 1).width = Math.min(maxLen + 3, 40);
  }
  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

export async function downloadGroupedZip<Row>(
  baseName: string,
  columns: ColumnDef<Row>[],
  rows: Row[],
  groupColId: string,
  groupLabel?: string,
): Promise<void> {
  const col = columns.find((c) => c.id === groupColId);
  if (!col) return;
  const groups = new Map<string, Row[]>();
  for (const r of rows) {
    const key = String(col.accessor(r) ?? "onbekend");
    const arr = groups.get(key);
    if (arr) arr.push(r);
    else groups.set(key, [r]);
  }

  const fmt = getExportFormat();
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  const date = new Date().toISOString().slice(0, 10);
  const ext = fmt === "xlsx" ? "xlsx" : "csv";
  for (const [group, groupRows] of groups) {
    if (fmt === "xlsx") {
      zip.file(`${slugify(group)}.xlsx`, await buildExcelBuffer(columns, groupRows));
    } else {
      zip.file(`${slugify(group)}.csv`, buildCsv(columns, groupRows));
    }
  }
  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, `${baseName}-per-${slugify(groupLabel ?? "groep")}-${date}.zip`);
}

// ── column config (visible + order) persisted per dataset ──

const COLS_KEY = (dataset: string) => `kuvo.console.cols.${dataset}.v1`;

export function defaultVisibleIds<Row>(columns: ColumnDef<Row>[]): string[] {
  return columns.filter((c) => c.alwaysVisible || c.defaultVisible).map((c) => c.id);
}

/** Visible-column ids (ordered) for a dataset, validated against the current column set. Always-
 *  visible columns are forced in; unknown saved ids are dropped; falls back to defaults. */
export function useColumnConfig<Row>(datasetKey: string, columns: ColumnDef<Row>[]) {
  const valid = useCallback(
    (ids: string[]): string[] => {
      const known = new Set(columns.map((c) => c.id));
      const always = columns.filter((c) => c.alwaysVisible).map((c) => c.id);
      const kept = ids.filter((id) => known.has(id));
      const withAlways = [...new Set([...always, ...kept])];
      return withAlways.length ? withAlways : defaultVisibleIds(columns);
    },
    [columns],
  );

  const [visibleIds, setVisibleIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(COLS_KEY(datasetKey));
      if (raw) return valid(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    return defaultVisibleIds(columns);
  });

  // Re-validate when the dataset changes (different column set).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(COLS_KEY(datasetKey));
      setVisibleIds(raw ? valid(JSON.parse(raw)) : defaultVisibleIds(columns));
    } catch {
      setVisibleIds(defaultVisibleIds(columns));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetKey]);

  const persist = useCallback(
    (ids: string[]) => {
      const next = valid(ids);
      setVisibleIds(next);
      try {
        localStorage.setItem(COLS_KEY(datasetKey), JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [datasetKey, valid],
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(COLS_KEY(datasetKey));
    } catch {
      /* ignore */
    }
    setVisibleIds(defaultVisibleIds(columns));
  }, [datasetKey, columns]);

  return { visibleIds, setVisibleIds: persist, reset };
}
